import type { JSX } from 'react';

import { Badge, Logo, Text } from '@change/ds-core-ui';
import { CheckIcon, CloseIcon } from '@change/ds-icons';
import { cn } from '@change/ds-utils';

type ComparisonCardProps = {
	title: string;
	isChangeOrg?: boolean;
	features: readonly string[];
};

export function ComparisonCard({ title, isChangeOrg = false, features }: ComparisonCardProps): JSX.Element {
	return (
		<div
			className={cn(
				'relative flex flex-1 flex-col rounded-lg',
				isChangeOrg
					? 'border-stroke-weaker bg-background-base elevation-elevated border px-6 pt-4 pb-9 md:z-10 md:min-h-[424px] md:pt-8'
					: 'bg-background-raised p-6 pt-7 pb-9',
			)}
		>
			<div className={cn('flex flex-col', isChangeOrg ? 'md:translate-y-1' : 'md:-translate-y-2')}>
				<div className={cn('mt-3 mb-6 flex flex-wrap items-center gap-3', { 'md:pl-24': !isChangeOrg })}>
					{isChangeOrg && <Logo ariaLabel="Change.org" color="primary" size="medium" variant="symbol" />}
					<Text as="h3" variant="heading" size="x-small">
						{title}
					</Text>
					{isChangeOrg && <Badge variant="success" emphasis="weaker" size="medium" label="No Fees" />}
				</div>
				<ul className={cn('flex flex-col gap-6', { 'md:pl-24': !isChangeOrg })}>
					{features.map((feature) => (
						<li key={feature} className="flex items-start gap-3">
							{isChangeOrg ? (
								<CheckIcon aria-label="Feature included" className="text-icon-strong mt-0.5 shrink-0" size="medium" />
							) : (
								<CloseIcon
									aria-label="Feature not included"
									className="text-icon-strong mt-0.5 shrink-0"
									size="medium"
								/>
							)}
							<Text variant="body" size="medium">
								{feature}
							</Text>
						</li>
					))}
				</ul>
			</div>
		</div>
	);
}
